import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { OrganizationService } from 'src/app/@shared/services/organization.service';

@Injectable({
  providedIn: 'root'
})

export class OfferDetailService {
  private loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private offerIdBehaviorSubject = new BehaviorSubject<string>('');
  private reloadBehaviorSubject = new BehaviorSubject<string>('');
  private offerDetailsBehaviorSubject = new BehaviorSubject<any>(null);
  private iaCopyPatch: { offerId: string; requestId: string } | null = null;

  public search$ = this.offerIdBehaviorSubject.asObservable();
  public isLoading$ = this.loadingBehaviorSubject.asObservable();
  public offerDetails$ = this.offerDetailsBehaviorSubject.asObservable();


  constructor(private httpClient: HttpClient, private organizationService: OrganizationService) { }

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public params$ = combineLatest([
    this.offerIdBehaviorSubject.pipe(),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([offerId, reload]) => {
      return offerId;
    })
  );


  private offerResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)),
    switchMap((_params) =>
      _params != 'reset' ? this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/${_params}?$expand=Detail`) : of(null)
    ),
    tap(() => this.loadingBehaviorSubject.next(false)),
    shareReplay(1)
  );

  public offer$: Observable<any> = this.offerResponse$.pipe(
    map((offer) => this.applyIACopyPatch(offer))
  );

  setIACopyRequestIdPatch(offerId: string, requestId: string): void {
    const id = offerId?.trim();
    const request = requestId?.trim();
    if (!id || !request) {
      return;
    }
    this.iaCopyPatch = { offerId: id, requestId: request };
  }

  getIACopyRequestIdPatch(offerId: string): string | null {
    const id = offerId?.trim();
    if (!id || this.iaCopyPatch?.offerId !== id) {
      return null;
    }
    return this.iaCopyPatch.requestId;
  }

  clearIACopyRequestIdPatch(): void {
    this.iaCopyPatch = null;
  }

  private applyIACopyPatch(offer: any): any {
    const patch = this.iaCopyPatch;
    if (!offer || !patch || offer.Detail?.Id !== patch.offerId) {
      return offer;
    }
    if (!offer.IACopyRequestId?.trim()) {
      return { ...offer, IACopyRequestId: patch.requestId };
    }
    return offer;
  }

  reset() {
    this.offerIdBehaviorSubject.next('reset');
    this.clearIACopyRequestIdPatch();
  }

  offerorPromoId(id: any) {
    this.offerIdBehaviorSubject.next(id);
  }

  // reloads/refreshes the divison listing
  reload() {
    // reload the divison data
    this.reloadBehaviorSubject.next(uuidv4());
  }
}
